import TwoButton from '@/components/TwoButton';
import { style } from '@/styles/globals';
import { pr } from '@/utils/pxToRem';
import AirlineSeatReclineExtraOutlinedIcon from '@mui/icons-material/AirlineSeatReclineExtraOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import {
  Checkbox,
  Stack,
  SwipeableDrawer,
  SwipeableDrawerProps,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import styled from '@mui/styled-engine';
import Box from '@mui/system/Box';
import { useState } from 'react';
import { mappingTextFromSeatClass } from '../../../utils/seatClassMapping';
// import { LeadingIcon } from '../../../../public/icons';
import type { SeatClassType } from './type';
import {
  Caption,
  Headline,
} from '../index';
import Image from '../Image';
import { ILangObj } from '@/slices/goyoloSeo/sliceType';

const tripDateIcon = 'https://storage.googleapis.com/goyolo-uat/images/SeoVexImprove/seat.svg';

const StyledBox = styled(Box)({
  background: style.color.black7,
  borderRadius: pr`6px`,
  display: 'flex',
  alignItems: 'flex-end',
  marginTop: pr`8px`,
  transitionDelay: '0.1s',
  '&:hover': {
    background: '#EEF2F4',
  },
});
const Container = styled('div')`
  display: flex;
  flex-direction: row;

  .icon-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px 16px;
  }

  .departure-date-select {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 64px;
    justify-content: center;
  }
`;

export const SelectStyledBox = styled(StyledBox)({
  // paddingTop: '10px',
  alignItems: 'center',
  boxSizing: 'border-box',
  height: 46,
  '.css-1r9yfjo-MuiSelect-select-MuiInputBase-input-MuiInput-input': {
    paddingRight: '8px !important',
  },
});

const StyledMenuBox = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
});
const StyledSelect = styled(Select)`
  div.MuiSelect-select.Mui-disabled {
    -webkit-text-fill-color: ${style.color.black2} !important;
  }
`;
const StyleDiv = styled('div')`
display: flex;
flex-direction: column;
height: 64px;
justify-content: center;
height: 56px;
`;

const MenuItemLayout = ({
  label,
  caption,
  checked,
  onCheck,
}: {
  label: string;
  caption: string;
  checked: boolean;
  onCheck: () => void;
}) => {
  const handleAdjustSeatClass = () => {
    onCheck();
  };
  return (
    <Stack
      onClick={(e) => {
        e.stopPropagation();
        handleAdjustSeatClass();
      }}
      alignItems="center"
      direction="row"
      spacing={pr`16px`}
    >
      <Stack
        style={{
          justifyContent: 'start',
          flexBasis: '90%',
        }}
      >
        <Typography
          variant="body2"
          fontWeight="bold"
        >
          {label}
        </Typography>
        <Typography variant="caption">{caption}</Typography>
      </Stack>
      <StyledMenuBox
        style={{
          justifyContent: 'end',
          flexBasis: '10%',
        }}
      >
        <Checkbox
          style={{ color: style.color.blue3 }}
          icon={<RadioButtonUncheckedOutlinedIcon />}
          checkedIcon={<RadioButtonCheckedOutlinedIcon />}
          checked={checked}
        />
      </StyledMenuBox>
    </Stack>
  );
};

const BottomDrawer = ({ children, ...props }: SwipeableDrawerProps) => (
  <SwipeableDrawer
    {...props}
    anchor="bottom"
    PaperProps={{
      ...props.PaperProps,
      style: {
        ...props.PaperProps?.style,
        width: '100%',
        margin: 0,
        position: 'absolute',
        left: 0,
        bottom: 0,
        background: 'FFFFFF',
        boxShadow:
          '0px 0px 0px 1px rgba(28, 59, 84, 0.05), 0px -2px 6px -2px rgba(28, 59, 84, 0.04), 0px -8px 12px -3px rgba(28, 59, 84, 0.08)',
        borderRadius: '16px 16px 0px 0px',
      },
    }}
  >
    {children}
  </SwipeableDrawer>
);

const ChooseSeatClass = ({
  seatClass,
  adjustSeatClass,
  lang,
  switchUpdateSecondTime,
}: {
  seatClass: SeatClassType;
  adjustSeatClass: (param: SeatClassType) => void;
  lang?: ILangObj;
  switchUpdateSecondTime?: any;
}) => {
  const [openSeatClassDialog, setOpenSeatClassDialog] = useState(false);
  /* eslint-disable max-len */

  const [checkedOption, setCheckedOption] = useState<SeatClassType>(seatClass);
  return ( //
    <Container className="date-input-container">
      <div className="icon-container">
        <Image
          imageSrc={tripDateIcon}
          alt="trip-date-icon"
          className="trip-date-icon"
          imageProps={{
            width: 24,
            height: 24,
          }}
        />
      </div>
      <StyleDiv onClick={() => {
        setOpenSeatClassDialog(true);
        if (switchUpdateSecondTime) {
          switchUpdateSecondTime();
        }
      }}
      >
        <Caption className="color--medium-sub">
          {lang?.SearchBar?.flightsTab?.seatClass?.title || 'Hạng ghế'}
        </Caption>
        <Headline
          className="color--darkness"
        >
          {mappingTextFromSeatClass(seatClass, lang)}
        </Headline>
      </StyleDiv>
      <BottomDrawer
        onOpen={() => { }}
        onClose={() => {
          setOpenSeatClassDialog(false);
          setCheckedOption(seatClass);
        }}
        open={openSeatClassDialog}
      >
        <Stack
          spacing="1.75em"
          sx={{ p: '1.5em 1em' }}
        >
          <Stack
            spacing={pr`16px`}
          >
            <Stack>
              <Typography variant="h6">{lang?.SearchBar?.flightsTab?.seatClass?.title || 'Loại vé'}</Typography>
            </Stack>
            <Stack spacing="1.2em">
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.seatClass?.economy || 'Phổ thông'}
                caption={lang?.SearchBar?.flightsTab?.seatClass?.economyDesc || 'Bay tiết kiệm, đáp ứng mọi nhu cầu cơ bản của bạn'}
                checked={checkedOption === 'PT'}
                onCheck={() => setCheckedOption('PT')}
              />
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.seatClass?.premiumEconomy || 'Phổ thông đặc biệt'}
                caption={lang?.SearchBar?.flightsTab?.seatClass?.premiumEconomyDesc || 'Chi phí hợp lý với bữa ăn ngon và chỗ để chân rộng rãi'}
                checked={checkedOption === 'PTDB'}
                onCheck={() => setCheckedOption('PTDB')}
              />
              <MenuItemLayout
                label={lang?.SearchBar?.flightsTab?.seatClass?.business || 'Thương gia'}
                caption={lang?.SearchBar?.flightsTab?.seatClass?.businessDesc || 'Bay đẳng cấp, với quầy làm thủ tục và khu ghế ngồi riêng'}
                checked={checkedOption === 'TG'}
                onCheck={() => setCheckedOption('TG')}
              />
              {/* enable if international route, support later
          <MenuItemLayout
            label="Hạng nhất"
            type="First Class"
            caption="Hạng cao cấp nhất, với dịch vụ 5 sao được cá nhân hóa"
          /> */}
            </Stack>
          </Stack>
          <TwoButton
            leftTitle={lang?.global?.cancel || 'Huỷ'}
            leftVariant="contained"
            rightVariant="contained"
            rightTitle={lang?.global?.apply || 'Áp dụng'}
            onLeftClick={() => {
              setOpenSeatClassDialog(false);
              setCheckedOption(seatClass);
            }}
            onRightClick={() => {
              setOpenSeatClassDialog(false);
              adjustSeatClass(checkedOption);
            }}
          />

        </Stack>
      </BottomDrawer>
    </Container>
  );
};

export default ChooseSeatClass;
